import { useState } from 'react';
import { Settings2, WalletCards } from 'lucide-react';

export type StrategyProfile = 'day-swing' | 'swing' | 'conservative';
export type Currency = 'USD' | 'TWD';

export interface BrokerProfile {
  id: 'generic-us' | 'generic-tw' | 'custom';
  allowFractionalShares: boolean;
  allowOddLot: boolean;
  twBoardLotSize: number;
  useMargin: boolean;
}

export interface TradingAccount {
  market: 'us' | 'tw';
  currency: Currency;
  cash: number;
  totalEquity?: number;
  reserveCashPct: number;
  maxDeployPctPerDay: number;
  broker: BrokerProfile;
}

export interface BuyingPower {
  accounts: {
    us?: TradingAccount;
    tw?: TradingAccount;
  };
  allowCrossCurrency: boolean;
  fxRates: Partial<Record<'USDTWD' | 'TWDUSD', number>>;
}

export interface BuyingPowerSettingsValue {
  buyingPower: BuyingPower;
  strategyProfile: StrategyProfile;
}

export const BUYING_POWER_STORAGE_KEY = 'invest-copilot.buyingPower.v1';
/** 同分頁即時通知購買力 / 策略變更（cross-tab 走 storage event）。 */
export const BUYING_POWER_CHANGE_EVENT = 'invest-copilot:buyingpower-changed';

export function defaultBuyingPowerSettings(): BuyingPowerSettingsValue {
  return {
    strategyProfile: 'day-swing',
    buyingPower: {
      accounts: {
        us: {
          market: 'us',
          currency: 'USD',
          cash: 15000,
          reserveCashPct: 0,
          maxDeployPctPerDay: 1,
          broker: {
            id: 'generic-us',
            allowFractionalShares: false,
            allowOddLot: false,
            twBoardLotSize: 1000,
            useMargin: false,
          },
        },
        tw: {
          market: 'tw',
          currency: 'TWD',
          cash: 500000,
          reserveCashPct: 0,
          maxDeployPctPerDay: 1,
          broker: {
            id: 'generic-tw',
            allowFractionalShares: false,
            allowOddLot: true,
            twBoardLotSize: 1000,
            useMargin: false,
          },
        },
      },
      allowCrossCurrency: false,
      fxRates: {},
    },
  };
}

export function loadBuyingPowerSettings(): BuyingPowerSettingsValue {
  const fallback = defaultBuyingPowerSettings();
  try {
    const raw = localStorage.getItem(BUYING_POWER_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<BuyingPowerSettingsValue>;
    return mergeSettings(fallback, parsed);
  } catch {
    return fallback;
  }
}

export function saveBuyingPowerSettings(value: BuyingPowerSettingsValue): void {
  try {
    localStorage.setItem(BUYING_POWER_STORAGE_KEY, JSON.stringify(value));
    // 同分頁元件（MyThesesPanel 的風險指標依策略顯示）即時同步。
    window.dispatchEvent(new Event(BUYING_POWER_CHANGE_EVENT));
  } catch { /**/ }
}

function mergeSettings(base: BuyingPowerSettingsValue, incoming: Partial<BuyingPowerSettingsValue>): BuyingPowerSettingsValue {
  // 簡化設定：使用者填的「可用」金額即為可動用全額，不再自動保留 / 折減（reserve=0、deploy=1）。
  const norm = (a: TradingAccount): TradingAccount => ({ ...a, reserveCashPct: 0, maxDeployPctPerDay: 1 });
  return {
    strategyProfile: incoming.strategyProfile === 'swing' || incoming.strategyProfile === 'conservative' || incoming.strategyProfile === 'day-swing'
      ? incoming.strategyProfile
      : base.strategyProfile,
    buyingPower: {
      allowCrossCurrency: incoming.buyingPower?.allowCrossCurrency ?? base.buyingPower.allowCrossCurrency,
      fxRates: incoming.buyingPower?.fxRates ?? base.buyingPower.fxRates,
      accounts: {
        us: norm({ ...base.buyingPower.accounts.us!, ...incoming.buyingPower?.accounts?.us, broker: { ...base.buyingPower.accounts.us!.broker, ...incoming.buyingPower?.accounts?.us?.broker } }),
        tw: norm({ ...base.buyingPower.accounts.tw!, ...incoming.buyingPower?.accounts?.tw, broker: { ...base.buyingPower.accounts.tw!.broker, ...incoming.buyingPower?.accounts?.tw?.broker } }),
      },
    },
  };
}

function money(n: number, currency: Currency): string {
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: currency === 'TWD' ? 0 : 2 })}`;
}

function deployAmount(account: TradingAccount | undefined): number {
  if (!account) return 0;
  return account.cash * (1 - account.reserveCashPct) * account.maxDeployPctPerDay;
}

export function BuyingPowerSettings({
  value,
  onChange,
  activeMarket,
  open: controlledOpen,
  onClose,
}: {
  value: BuyingPowerSettingsValue;
  onChange: (next: BuyingPowerSettingsValue) => void;
  activeMarket?: 'us' | 'tw';
  /** 受控展開：父層（hero 的「調整」鈕）控制開合。傳入時不再顯示自帶的收合 bar。 */
  open?: boolean;
  onClose?: () => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : internalOpen;
  const us = value.buyingPower.accounts.us;
  const tw = value.buyingPower.accounts.tw;

  function update(next: BuyingPowerSettingsValue) {
    onChange(next);
    saveBuyingPowerSettings(next);
  }

  function updateAccount(market: 'us' | 'tw', patch: Partial<TradingAccount>) {
    const current = value.buyingPower.accounts[market];
    if (!current) return;
    update({
      ...value,
      buyingPower: {
        ...value.buyingPower,
        accounts: {
          ...value.buyingPower.accounts,
          [market]: { ...current, ...patch },
        },
      },
    });
  }

  // 受控模式（hero 調整鈕）：直接顯示精簡設定卡，不顯示自帶收合 bar。
  if (controlled) {
    if (!open) return null;
    return (
      <section className="card p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-sm font-bold text-(--text-primary)">
            <WalletCards size={14} className="text-(--accent)" /> 購買力 / 策略
          </span>
          <button type="button" onClick={() => onClose?.()} className="text-[11px] text-(--text-muted) hover:text-(--accent)">收起</button>
        </div>
        <SimpleBuyingPower value={value} us={us} tw={tw} onStrategy={(p) => update({ ...value, strategyProfile: p })} onCash={updateAccount} />
      </section>
    );
  }

  // 非受控（保留舊用法相容）：可收合 bar。
  const current = value.buyingPower.accounts[activeMarket ?? 'us'];
  const currentCurrency: Currency = (activeMarket ?? 'us') === 'tw' ? 'TWD' : 'USD';
  const currentDeploy = deployAmount(current);
  return (
    <section className="card p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setInternalOpen(!internalOpen)}
        className="w-full px-3 py-2 flex items-center justify-between gap-3 hover:bg-(--bg-soft) transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0 text-left">
          <WalletCards size={15} className="text-(--accent) shrink-0" />
          <span className="font-semibold text-sm text-(--text-primary)">購買力設定</span>
          <span className="text-[10px] text-(--text-muted) truncate">
            {money(current?.cash ?? 0, currentCurrency)} · 今日最多 {money(currentDeploy, currentCurrency)} · {strategyLabel(value.strategyProfile)}
          </span>
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-(--accent)">
          <Settings2 size={12} /> {internalOpen ? '收起' : '調整'}
        </span>
      </button>
      {internalOpen && (
        <div className="px-3 pb-3 border-t border-(--border) pt-3">
          <SimpleBuyingPower value={value} us={us} tw={tw} onStrategy={(p) => update({ ...value, strategyProfile: p })} onCash={updateAccount} />
        </div>
      )}
    </section>
  );
}

/** 精簡購買力設定：只問「美股 USD 可用 / 台股 TWD 可用 / 策略」，其餘風控用預設。 */
function SimpleBuyingPower({
  value,
  us,
  tw,
  onStrategy,
  onCash,
}: {
  value: BuyingPowerSettingsValue;
  us?: TradingAccount;
  tw?: TradingAccount;
  onStrategy: (p: StrategyProfile) => void;
  onCash: (market: 'us' | 'tw', patch: Partial<TradingAccount>) => void;
}) {
  return (
    <div className="space-y-2.5">
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-[11px] text-(--text-muted)">
          策略
          <select
            value={value.strategyProfile}
            onChange={(e) => onStrategy(e.target.value as StrategyProfile)}
            className="mt-1 h-9 w-full rounded border border-(--border) bg-(--bg-card) px-2 text-sm font-bold text-(--text-primary)"
          >
            <option value="day-swing">短線</option>
            <option value="swing">波段</option>
            <option value="conservative">保守</option>
          </select>
        </label>
        <label className="text-[11px] text-(--text-muted)">
          🇺🇸 美股可用 (USD)
          <input
            type="number" min={0} step={500}
            value={Number.isFinite(us?.cash) ? us!.cash : 0}
            onChange={(e) => onCash('us', { cash: Math.max(0, Number(e.target.value) || 0) })}
            className="mt-1 h-9 w-full rounded border border-(--border) bg-(--bg-card) px-2 text-sm font-bold text-(--text-primary) tnum"
          />
        </label>
        <label className="text-[11px] text-(--text-muted)">
          🇹🇼 台股可用 (TWD)
          <input
            type="number" min={0} step={10000}
            value={Number.isFinite(tw?.cash) ? tw!.cash : 0}
            onChange={(e) => onCash('tw', { cash: Math.max(0, Number(e.target.value) || 0) })}
            className="mt-1 h-9 w-full rounded border border-(--border) bg-(--bg-card) px-2 text-sm font-bold text-(--text-primary) tnum"
          />
        </label>
      </div>
      <div className="text-[10px] text-(--text-muted) leading-relaxed rounded border border-(--warn)/30 bg-(--warn)/5 px-2 py-1.5">
        🐧 規則：能買整張就買整張，台股買不起自動用零股、美股需整股。USD 只買美股、TWD 只買台股（不換匯）。設定只存這台瀏覽器，不上傳券商、不自動下單。
      </div>
    </div>
  );
}

function strategyLabel(profile: StrategyProfile): string {
  if (profile === 'day-swing') return '短線';
  if (profile === 'swing') return '波段';
  return '保守';
}